'use client';

import { useRef, useState } from 'react';
import { transformCloudinaryUrl } from 'shared/types/utils/cloudinary';
import BrandImage from './brand-image';
import CtaChevron from './cta-chevron';
import { externalLinkProps } from './external-link';

interface BannerAction {
  label: string;
  href: string;
}

export interface BannerSlide {
  image: string;
  /**
   * Optional portrait crop for phones (the banner is 2:3 there). When set it is served below md
   * via <picture> below md, so phones never downloads the wide crop and the art direction matches Figma.
   */
  mobileImage?: string;
  /** Bottom-left label, e.g. "Glass Railing". */
  label: string;
  /** One supporting line beneath the label. */
  subtitle?: string;
  /** Primary dark button, bottom-right. */
  button?: BannerAction;
  /** Secondary text link beside the button. */
  link?: BannerAction;
  /** 'light' for slides over a light image (dark text); defaults to 'dark' (white text). */
  theme?: 'light' | 'dark';
  /** Overrides `theme` below lg — for photos whose phone crop is darker (or lighter) than the wide one. */
  mobileTheme?: 'light' | 'dark';
  /**
   * Phones only, single-slide heroes: show the photo as a 4:3 band cropped to its bottom edge and
   * put the copy and actions beneath it instead of over it — for photos whose subject sits at the
   * bottom of the frame (a stair's treads) where overlaid copy would cover it. Ignored at lg+.
   */
  mobileCopyBelow?: boolean;
  /** Skip the bottom fade entirely — for photos that already carry the copy without help. */
  noFade?: boolean;
  /**
   * Which part of the photo to keep when it's cropped to the banner ratio; defaults to center.
   * On phones the banner is portrait, so a landscape photo shows its full height and `object-position`
   * can only pan sideways — 'bottom' therefore also zooms the phone (1.35×) and tablet (1.3×) crops toward
   * the lower part of the photo so a ceiling or sky band doesn't take the top third. 'lower' sits
   * between the two (about two-thirds down) for a subject just below the photo's middle; 'upper' is
   * its mirror (about a quarter down) for a subject in the photo's upper half.
   */
  imagePosition?: 'upper' | 'center' | 'lower' | 'bottom';
}

// Horizontal distance (px) a touch has to travel before it counts as a swipe rather than a tap.
const SWIPE_THRESHOLD = 40;

// Manually-advanced hero carousel at the top of category pages. Desktop follows the Figma: full-bleed
// 21:9 slides (Figma drew 1512×703; snapped to the ratio palette), bottom-left GT-Bold label, a dark 177×37 CTA + "Compare Systems >" link 168px in
// from the right, and 15px dots, with no scrim. On phones the label, a full-width CTA and the link
// stack over a bottom gradient (the photo alone doesn't guarantee legible white text on a 375px crop),
// and the slides swipe. No autoplay — the shopper drives it. Crossfade so wrapping past the last
// slide doesn't rewind through all of them. Content is passed in (repo config today, Contentful later).
const BannerCarousel = ({ slides }: { slides: BannerSlide[] }) => {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  if (!slides.length) return null;
  const count = slides.length;
  // A lone slide is the page's hero, so its label is the page heading.
  const Heading = count === 1 ? 'h1' : 'h2';

  const go = (index: number) => setActive((index + count) % count);
  // Stacked phone layout only works for a lone slide (the slides are absolutely stacked otherwise).
  const stacked = count === 1 && !!slides[0].mobileCopyBelow;


  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    go(delta < 0 ? active + 1 : active - 1);
  };

  return (
    <section className="relative w-full font-gt" aria-roledescription="carousel" aria-label="Featured railing systems">
      <div
        className={`relative w-full overflow-hidden lg:aspect-[7/2] ${stacked ? '' : 'aspect-square md:aspect-[4/3]'}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {slides.map((slide, i) => {
          const light = slide.theme === 'light';
          const mobileLight = (slide.mobileTheme ?? slide.theme) === 'light';
          // Copy colour per breakpoint: the phone/tablet theme below lg, the desktop theme from lg.
          const textColor = `${mobileLight ? 'text-[#212121]' : 'text-white'} ${light ? 'lg:text-[#212121]' : 'lg:text-white'}`;
          return (
            <div
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}: ${slide.label}`}
              aria-hidden={i !== active}
              className={`transition-opacity duration-500 lg:absolute lg:inset-0 ${
                stacked ? 'relative flex flex-col' : 'absolute inset-0'
              } ${i === active ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            >
              <div className={stacked ? 'relative aspect-[4/3] w-full md:aspect-[16/9] lg:absolute lg:inset-0 lg:aspect-auto' : 'absolute inset-0'}>
              {slide.mobileImage ? (
                <picture>
                  <source media="(min-width: 640px)" srcSet={transformCloudinaryUrl(slide.image, { width: 2400 })} />
                  {/* eslint-disable-next-line @next/next/no-img-element -- art-directed /public asset; see BrandImage */}
                  <img
                    src={transformCloudinaryUrl(slide.mobileImage, { width: 1200 })}
                    alt={slide.label}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    fetchPriority={i === 0 ? 'high' : undefined}
                    className={`absolute inset-0 size-full object-cover ${stacked ? 'object-bottom lg:object-center' : 'object-center'}`}
                  />
                </picture>
              ) : (
                <BrandImage
                  src={slide.image}
                  alt={slide.label}
                  width={2000}
                  sizes="100vw"
                  quality="auto:good"
                  loading={i === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 size-full object-cover ${
                    slide.imagePosition === 'bottom'
                      ? 'origin-bottom object-bottom max-md:scale-[1.35] md:max-lg:scale-[1.3]'
                      : slide.imagePosition === 'lower'
                        ? 'object-[50%_68%]'
                        : slide.imagePosition === 'upper'
                          ? 'object-[50%_24%]'
                          : 'object-center'
                  }`}
                />
              )}
              {/* Bottom fade under the copy. Strong on phones, where the copy block covers most of the
                  photo; a lighter, shorter fade at lg+ so the copy reads without hiding the subject
                  (the treads at the foot of the stair) behind a solid band. Light slides fade to white. */}
              {!slide.noFade && (
              <div
                className={`absolute inset-x-0 bottom-0 h-3/4 lg:h-1/2 ${stacked ? 'hidden lg:block' : ''} ${
                  mobileLight
                    ? 'bg-gradient-to-t from-white/85 via-white/55 to-transparent'
                    : 'bg-gradient-to-t from-black/70 via-black/40 to-transparent'
                } ${light ? 'lg:from-white/75 lg:via-white/30' : 'lg:from-black/55 lg:via-black/20'
                }`}
              />
              )}
              </div>

              <div
                className={`flex flex-col justify-end lg:absolute lg:inset-0 lg:pb-60 ${
                  stacked ? 'relative pb-32 pt-24' : 'absolute inset-0 pb-40'
                }`}
              >
                <div className="mx-auto flex w-full flex-col gap-32 px-24 lg:flex-row lg:items-end lg:justify-between lg:gap-24 lg:pl-70 lg:pr-168">
                  <div>
                    <Heading className={`font-gt text-40 font-bold leading-[1.1] lg:text-36 lg:leading-[1.2] ${stacked ? 'max-lg:text-[#212121]' : ''} ${textColor}`}>{slide.label}</Heading>
                    {slide.subtitle && (
                      <p className={`mt-16 max-w-[520px] text-18 leading-[1.35] lg:mt-8 lg:text-16 ${stacked ? 'max-lg:text-[#5e5e5e]' : ''} ${textColor}`}>
                        {slide.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Phones follow the marketing hero: a white button and the text link share one row.
                      Desktop keeps the Figma dark 177×37 button. */}
                  {(slide.button || slide.link) && (
                    <div className="flex shrink-0 flex-wrap items-center gap-x-16 gap-y-12 lg:flex-nowrap lg:gap-36">
                      {slide.button && (
                        <a
                          href={slide.button.href}
                          {...externalLinkProps(slide.button.href)}
                          className={`inline-flex h-48 flex-1 items-center justify-center whitespace-nowrap px-16 text-14 font-medium transition-opacity hover:opacity-90 lg:h-37 lg:w-177 lg:flex-none lg:bg-[#1c1c1c] lg:px-0 lg:text-12 lg:font-normal lg:text-white ${
                            mobileLight || stacked ? 'bg-[#1c1c1c] text-white' : 'bg-white text-[#212121]'
                          }`}
                        >
                          {slide.button.label}
                        </a>
                      )}
                      {slide.link && (
                        <a
                          href={slide.link.href}
                          {...externalLinkProps(slide.link.href)}
                          className={`inline-flex min-h-44 shrink-0 items-center whitespace-pre text-14 font-medium transition-opacity hover:opacity-70 lg:min-h-0 lg:text-13 ${stacked ? 'max-lg:text-[#212121]' : ''} ${textColor}`}
                        >
                          {slide.link.label}
                          <CtaChevron />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Dots: 15px visual, 9px apart, 20px off the bottom edge — but each sits in a 44px hit area. */}
        {count > 1 && (
          <div className="absolute inset-x-0 bottom-[5.5px] flex justify-center lg:bottom-[15.5px]">
            {slides.map((slide, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to ${slide.label}`}
                aria-current={i === active}
                onClick={() => go(i)}
                className="flex size-44 items-center justify-center lg:size-24"
              >
                <span
                  className={`block size-15 rounded-full transition-colors ${
                    i === active ? 'bg-[#3d3d3d]' : 'bg-[#d9d9d9] hover:bg-white'
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>

    </section>
  );
};

export default BannerCarousel;
